import React, { ChangeEventHandler, useState, useEffect, useRef } from "react";
import { Space, Typography, Input, Row, Col, Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import CreatorSavedResponsesModalComponent from "../CreatorSavedResponsesModalComponent";
import { userService } from "../../services";
import "./index.less";

type props = {
  title?: string;
  value?: string | null;
  handleChange?: ChangeEventHandler<HTMLTextAreaElement>;
  renderFooter?: boolean;
};

const MessageTextAreaComponent: React.FC<props> = ({ value, handleChange, title, renderFooter = false }) => {
  const { t } = useTranslation();

  const [isSavedResponsesModalOpen, setIsSavedResponsesModalOpen] = useState<boolean>(false);
  const [isIdentifier, setIsIdentifier] = useState<boolean>(false);
  const [identifiers, setIdentifiers] = useState<any>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [validMentions, setValidMentions] = useState<Set<string>>(new Set());
  const textRef = useRef<any>(null);

  // Monitor text changes for @ identifier detection
  useEffect(() => {
    const words = value?.match(/(\S+|\s+)/g) || [];
    const lastWord = words[words.length - 1];

    if (lastWord) {
      if (lastWord === "@") {
        setIsIdentifier(true);
      } else if (lastWord.startsWith("@") && lastWord.length > 1) {
        setIsIdentifier(true);
        const wordWithoutAt = lastWord.slice(1);
        if (wordWithoutAt) {
          searchUser(wordWithoutAt);
        } else {
          setIdentifiers([]);
        }
      } else {
        setIdentifiers([]);
        setIsIdentifier(false);
      }
    } else {
      setIdentifiers([]);
      setIsIdentifier(false);
    }
  }, [value]);

  const searchUser = async (searchValue: string) => {
    setIsSearching(true);
    const payload = {
      q: searchValue,
      limit: 10,
    };
    await userService
      .search(payload)
      .then((res) => {
        setIdentifiers(res.data);
        setIsSearching(false);
      })
      .catch((err: any) => {
        console.log(err?.message);
        setIsSearching(false);
      });
  };

  const handleProfileClick = (username: string) => {
    setValidMentions((prev) => new Set(prev).add(username));

    const words = value?.match(/(\S+|\s+)/g) || [];
    if (words.length > 0) {
      const lastWord = words[words.length - 1];
      const updatedText = value?.replace(new RegExp(`${lastWord}$`), `@${username} `) || `@${username} `;
      if (handleChange) {
        const changeEvent: any = {
          target: {
            value: updatedText,
          },
        };
        handleChange(changeEvent);
      }
      textRef.current?.focus();
    }
  };

  const cleanInvalidMentions = (text: string, validUsernames: string[]) => {
    return text
      .split(/(\s+)/)
      .map((word) => {
        if (word.startsWith("@")) {
          const username = word.slice(1).trim();
          if (!username) return "";
          if (!validUsernames.includes(username)) return "";
          return word;
        }
        return word;
      })
      .join("")
      .replace(/\s+/g, " ")
      .trim();
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (handleChange) {
      handleChange(e);
    }
  };

  const selectQuickMessage = (message: string) => {
    if (message && !isEmpty(message) && handleChange) {
      const cleanedMessage = cleanInvalidMentions(message, Array.from(validMentions));
      const value: any = {
        target: {
          value: cleanedMessage,
        },
      };
      handleChange(value);
    }
  };

  const handleAddIdentification = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleChange) {
      const textarea = textRef.current;
      const currentText = value || "";
      const cursorPosition = textarea?.selectionStart || currentText.length;
      const beforeCursor = currentText.substring(0, cursorPosition);
      const afterCursor = currentText.substring(cursorPosition);
      const newText = beforeCursor + "@" + afterCursor;
      const event = {
        target: {
          value: newText,
        },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      handleChange(event);
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        }
      });
    }
  };

  const handleIdentificationMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleAddTag = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleChange) {
      const textarea = textRef.current;
      const currentText = value || "";
      const cursorPosition = textarea?.selectionStart || currentText.length;
      const beforeCursor = currentText.substring(0, cursorPosition);
      const afterCursor = currentText.substring(cursorPosition);
      const newText = beforeCursor + "/name" + afterCursor;

      const event = {
        target: {
          value: newText,
        },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      handleChange(event);
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(cursorPosition + 5, cursorPosition + 5);
        }
      });
    }
  };

  const handleTagMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <Space className="w-full gap-13" direction="vertical">
      <div className="messageTextAreaWrapper">
        <div className="message-script-text-area">
          {title && <h3 className="message-script-text-area__title">{title}</h3>}
          <div className="message-script-text-area__wrapper">
            <textarea
              ref={textRef}
              className="message-script-text-area__input"
              value={value || ""}
              onChange={handleTextAreaChange}
              placeholder={`${t("your-message")}....`}
              rows={5}
            />
            {renderFooter && (
              <div className="message-script-text-area__footer">
                <div onMouseDown={handleIdentificationMouseDown} onClick={handleAddIdentification}>
                  <span className="footer-text">@ </span>
                  <span className="footer-tag">Identification</span>
                </div>
                <div onMouseDown={handleTagMouseDown} onClick={handleAddTag}>
                  <span className="footer-text">Tag </span>
                  <span className="footer-tag">/name</span>
                </div>
              </div>
            )}
          </div>
        </div>
        {isIdentifier && renderFooter && (
          <Row className="identifiers-list-container">
            {isSearching ? (
              <Col span={24}>
                {[...Array(3)].map((_, index) => (
                  <Row key={index} className="justify-between items-center mt-10">
                    <Col span={24}>
                      <Row className="items-center">
                        <Skeleton.Avatar active size={40} shape="circle" style={{ background: "#333" }} />
                        <Space direction="vertical" className="gap-5" style={{ marginLeft: 12 }}>
                          <Skeleton.Input active size="small" style={{ width: 100, background: "#333" }} />
                          <Skeleton.Input active size="small" style={{ width: 150, background: "#333" }} />
                        </Space>
                      </Row>
                    </Col>
                  </Row>
                ))}
              </Col>
            ) : identifiers?.length !== 0 ? (
              <>
                {identifiers.map((creator: any) => (
                  <Col span={24} key={creator._id} className="creator-item">
                    <Row onClick={() => handleProfileClick(creator.username!)} className="creator-row">
                      <img className="avatar" src={creator.avatar || ""} alt={creator.avatar || "Creator Avatar"} />
                      <div className="creator-info">
                        <Typography.Text className="username">{creator.username || ""}</Typography.Text>
                        <Typography.Text className="name">{creator.name}</Typography.Text>
                      </div>
                    </Row>
                  </Col>
                ))}
              </>
            ) : (
              <Typography.Text className="no-creator-found">{t("no-creator-found")}</Typography.Text>
            )}
          </Row>
        )}
      </div>

      <CreatorSavedResponsesModalComponent
        isOpen={isSavedResponsesModalOpen}
        onCloseClick={() => setIsSavedResponsesModalOpen(false)}
        selectMessage={selectQuickMessage}
      />
    </Space>
  );
};

export default MessageTextAreaComponent;
